import { useLayoutEffect, type RefObject } from 'react';

/** Fits the field to its text and answers whether the text runs past one line. */
function fitHeight(node: HTMLTextAreaElement): boolean {
  node.style.height = 'auto';
  node.style.height = `${String(node.scrollHeight)}px`;
  const style = getComputedStyle(node);
  const oneLine = (parseFloat(style.lineHeight) || 0) + (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
  return oneLine > 0 && node.scrollHeight > oneLine + 1;
}

/**
 * Lets a text area grow with what is written in it, up to the `max-height` its stylesheet sets,
 * after which it scrolls. Measured before paint on every change of the value and whenever the
 * field's width changes, so a long prompt never ends up squeezed into its starting line. `onWraps`
 * hears, on every measure, whether the text runs past one line (the pill composer then puts its
 * controls under the text, P5 „Pisanie").
 */
export function useAutoGrow(field: RefObject<HTMLTextAreaElement | null>, value: string, onWraps?: (wraps: boolean) => void) {
  useLayoutEffect(() => {
    const node = field.current;
    if (node === null) return undefined;
    let width = node.clientWidth;
    onWraps?.(fitHeight(node));
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (node.clientWidth === width) return;
      width = node.clientWidth;
      onWraps?.(fitHeight(node));
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [field, value, onWraps]);
}
